import React, { FC, useState } from 'react';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { Events } from 'react-scroll';
import { Text, Divider, Button } from '@wix/design-system';
import { copyOfExploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { useBi } from '@components/Hooks/useBi';
import LinkWithHref from '../LinkWithHref';
import s from './NavBar.module.scss';
export const NAVBAR_HEIGHT_OFFSET = -146;

type Props = {
  isMobileView?: boolean;
  navigationRefs: any;
  copy?: (value: string) => Promise<void>;
  showBack?: boolean;
};

export const Sections = {
  EMPTY: '',
  ABOUT: 'about',
  CLIENT_PROJECTS: 'client-projects',
  SERVICES_AND_REVIEWS: 'services-and-reviews',
};

const TOP_TAB_TYPE = 'top tab';

const NavBar: FC<Props> = ({ isMobileView, navigationRefs, showBack }) => {
  const { t } = useTranslation();
  const logger = useBi();
  const { sessionId } = useBiSessionContext();
  const { entry, origin, isRenderedInModal, host, partnerProfile } =
    useProfileContext();
  const [navItemWasClicked, setNavItemWasClicked] = useState(Sections.ABOUT);

  const registerScrollEnds = (section: string) => {
    Events.scrollEvent.register('end', function (_to: any, _element: any) {
      setNavItemWasClicked(section);
      Events.scrollEvent.remove('end');
    });
  };

  const biGenericClick = (clickType: string, data?: string) => {
    logger.report(
      copyOfExploreResultsPageGenericClick({
        clickType,
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data,
      }),
    );
  };

  const handleSetActive = () => {
    setNavItemWasClicked(Sections.EMPTY);
  };

  const profileNavBarClassName = `${
    isMobileView
      ? s.scrollingWrapper
      : isRenderedInModal
      ? s.navnoHeader
      : s.nav
  } ${!isMobileView && !isRenderedInModal && showBack ? s.withBack : ''}`;

  return (
    <div className={profileNavBarClassName} data-hook="profile-navbar">
      <nav
        aria-label={t('profile-page-navigation-tabs-aria-label')}
        style={isMobileView ? { padding: '0 24px' } : { padding: '0 48px' }}
      >
        <ul style={{ margin: '12px 0' }}>
          <li>
            <Text>
              <LinkWithHref
                href="#about"
                className={`${s.navItem} ${
                  navItemWasClicked === Sections.ABOUT ? s.active : ''
                }`}
                weight="thin"
                size="small"
                offset={NAVBAR_HEIGHT_OFFSET}
                activeClass={navItemWasClicked ? '' : s.active}
                onSetActive={handleSetActive}
                smooth
                spy
                to={Sections.ABOUT}
                data-hook="navbar-about-tab"
                onClick={() => {
                  registerScrollEnds(Sections.ABOUT);
                  biGenericClick(TOP_TAB_TYPE, 'about tab');
                  navigationRefs.current?.[Sections.ABOUT].focus({
                    preventScroll: true,
                  });
                }}
              >
                <span>{t('profile-page-nav-bar-about')}</span>
              </LinkWithHref>
            </Text>
          </li>
          <li>
            <Text>
              <LinkWithHref
                href="#client-projects"
                className={`${s.navItem} ${
                  navItemWasClicked === Sections.CLIENT_PROJECTS ? s.active : ''
                }`}
                weight="thin"
                size="small"
                onSetActive={handleSetActive}
                offset={NAVBAR_HEIGHT_OFFSET}
                activeClass={navItemWasClicked ? '' : s.active}
                smooth
                spy
                to={Sections.CLIENT_PROJECTS}
                hashSpy
                onClick={() => {
                  registerScrollEnds(Sections.CLIENT_PROJECTS);
                  biGenericClick(TOP_TAB_TYPE, 'featured projects tab');
                  navigationRefs.current?.[Sections.CLIENT_PROJECTS].focus({
                    preventScroll: true,
                  });
                }}
              >
                {t('profile-page-nav-bar-client-projects')}
              </LinkWithHref>
            </Text>
          </li>
          <li>
            <Text>
              <LinkWithHref
                href="#services-and-reviews"
                className={`${s.navItem} ${
                  navItemWasClicked === Sections.SERVICES_AND_REVIEWS
                    ? s.active
                    : ''
                }`}
                weight="thin"
                size="small"
                onSetActive={handleSetActive}
                offset={NAVBAR_HEIGHT_OFFSET}
                activeClass={navItemWasClicked ? '' : s.active}
                smooth
                spy
                to={Sections.SERVICES_AND_REVIEWS}
                hashSpy
                onClick={() => {
                  registerScrollEnds(Sections.SERVICES_AND_REVIEWS);
                  navigationRefs.current?.[Sections.SERVICES_AND_REVIEWS].focus(
                    {
                      preventScroll: true,
                    },
                  );
                  biGenericClick(TOP_TAB_TYPE, 'services and reviews tab');
                }}
              >
                {t('profile-page-nav-bar-services-and-reviews')}
              </LinkWithHref>
            </Text>
          </li>
        </ul>
      </nav>
      {!isMobileView && <Divider />}
    </div>
  );
};

export default NavBar;
